import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Divider, Avatar, InputBase, IconButton, Paper,
  Stack, TextField, List, ListItem, ListItemAvatar, ListItemText,
  CircularProgress, Popover, Grid, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Tabs, Tab, Checkbox,
  useMediaQuery, useTheme, Badge, AvatarGroup, alpha,
} from "@mui/material";
import {
  Search, Send, AttachFile, EmojiEmotions, CheckCircle,
  Close as CloseIcon, Image as ImageIcon, Description as DocumentIcon,
  VideoLibrary as VideoIcon, Add as AddIcon, PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon, Chat as ChatIcon, Group as GroupIcon,
  Lock as LockIcon, PersonAddAlt1 as ConnectIcon,
  School as MentorIcon, Person as MenteeIcon,
  HourglassEmpty as PendingIcon, InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import TopBar from "./globalComponents/topbar";
import Footer from "./globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";
const STATUS_URL = `${API}/api/connection-requests/status`;

// Tab index constants
const TAB_MENTORS = 0;
const TAB_MENTEES = 1;
const TAB_GROUPS  = 2;

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
  group: "#7C3AED", // purple stays — purely decorative for group avatars
};

// ── Role Tag chip ─────────────────────────────────────────────────────────────
const RoleTag = ({ role, size = "small" }) => {
  const isMentor = role === "mentor";
  const bg = isMentor ? C.primarySoft : C.secondarySoft;
  const fg = isMentor ? C.primary : C.secondary;
  const Icon = isMentor ? MentorIcon : MenteeIcon;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: "11px !important", color: `${fg} !important` }} />}
      label={isMentor ? "Mentor" : "Mentee"}
      size={size}
      sx={{
        height: 18, fontSize: 10, fontWeight: 700, letterSpacing: 0.2,
        bgcolor: bg, color: fg,
        border: `1px solid ${alpha(fg, 0.2)}`,
        "& .MuiChip-label": { px: 0.75 },
        "& .MuiChip-icon": { ml: 0.5 },
      }}
    />
  );
};

// ── Message bubble ────────────────────────────────────────────────────────────
const Bubble = ({ msg, isSent, senderName, senderInitial }) => (
  <Box sx={{ mb: 1.5 }}>
    {!isSent && senderName && (
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textSecondary, ml: 5.5, mb: 0.25 }}>
        {senderName}
      </Typography>
    )}
    <Stack direction={isSent ? "row-reverse" : "row"} spacing={1} alignItems="flex-end">
      {!isSent && (
        <Avatar sx={{
          width: 30, height: 30,
          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
          color: "#fff",
          fontSize: 13, fontWeight: 700,
        }}>
          {senderInitial}
        </Avatar>
      )}
      <Box sx={{ maxWidth: { xs: "80%", sm: "68%" } }}>
        <Paper
          elevation={0}
          sx={{
            px: 1.75, py: 1,
            bgcolor: isSent ? C.primary : C.paper,
            color: isSent ? "#fff" : C.textPrimary,
            borderRadius: isSent ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
            border: isSent ? "none" : `1px solid ${C.border}`,
            boxShadow: isSent ? `0 2px 8px ${C.primary}20` : "none",
          }}
        >
          <Typography sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.55, wordBreak: "break-word" }}>
            {msg.text}
          </Typography>
        </Paper>
        <Stack direction={isSent ? "row-reverse" : "row"} alignItems="center" spacing={0.5} mt={0.25}>
          <Typography sx={{ fontSize: 10, color: C.textMuted }}>{msg.time}</Typography>
          {isSent && <CheckCircle sx={{ fontSize: 11, color: C.secondary }} />}
        </Stack>
      </Box>
    </Stack>
  </Box>
);

// ── Banners ───────────────────────────────────────────────────────────────────
const MenteeBlockedBanner = ({ mentorName, onNavigate }) => (
  <Box sx={{
    mx: { xs: 1.5, sm: 2.5 }, mt: 2, p: 2.5,
    borderRadius: 2.5, bgcolor: C.warningSoft,
    border: `1px solid ${alpha(C.warning, 0.3)}`,
    display: "flex", gap: 2, alignItems: "flex-start",
  }}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "50%",
      bgcolor: alpha(C.warning, 0.2),
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <LockIcon sx={{ fontSize: 20, color: C.warning }} />
    </Box>
    <Box flex={1}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#7A4F00", mb: 0.5 }}>
        Connection required to chat
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#946200", lineHeight: 1.6, mb: 1.75 }}>
        You can only message{mentorName ? ` ${mentorName}` : " this mentor"} after your connection
        request has been accepted. Send a request to get started.
      </Typography>
      <Button
        variant="contained"
        startIcon={<ConnectIcon sx={{ fontSize: 16 }} />}
        onClick={onNavigate}
        sx={{
          bgcolor: C.warning, color: "#fff", fontWeight: 700, fontSize: 13,
          textTransform: "none", borderRadius: 2.5, px: 2.5, py: 1, boxShadow: "none",
          "&:hover": { bgcolor: "#A06B07", boxShadow: "none" },
        }}
      >
        Send connection request
      </Button>
    </Box>
  </Box>
);

const MentorAwaitMenteeBanner = ({ menteeName }) => (
  <Box sx={{
    mx: { xs: 1.5, sm: 2.5 }, mt: 2, p: 2.5,
    borderRadius: 2.5, bgcolor: C.borderSoft,
    border: `1px solid ${C.border}`,
    display: "flex", gap: 2, alignItems: "flex-start",
  }}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "50%",
      bgcolor: alpha(C.textSecondary, 0.15),
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <InfoIcon sx={{ fontSize: 20, color: C.textSecondary }} />
    </Box>
    <Box flex={1}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary, mb: 0.5 }}>
        Waiting on a connection request
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.6 }}>
        You can chat with{menteeName ? ` ${menteeName}` : " this mentee"} once they send you a
        connection request and you accept it. Mentors can't start the connection — the request must
        come from the mentee.
      </Typography>
    </Box>
  </Box>
);

const PendingConnectionBanner = ({ mentorName }) => (
  <Box sx={{
    mx: { xs: 1.5, sm: 2.5 }, mt: 2, p: 2.5,
    borderRadius: 2.5, bgcolor: C.primarySofter,
    border: `1px solid ${C.primarySoft}`,
    display: "flex", gap: 2, alignItems: "flex-start",
  }}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "50%",
      bgcolor: C.primarySoft,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <PendingIcon sx={{ fontSize: 20, color: C.primary }} />
    </Box>
    <Box flex={1}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.primary, mb: 0.5 }}>
        Connection request pending
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.primaryDark, lineHeight: 1.6 }}>
        The connection request{mentorName ? ` with ${mentorName}` : ""} is awaiting
        approval. Chat unlocks once it's accepted.
      </Typography>
    </Box>
  </Box>
);

// ── Input bar ─────────────────────────────────────────────────────────────────
const InputBar = ({ message, setMessage, onSend, onEmojiOpen, onAttach, showAttach = true, disabled = false }) => (
  <Box sx={{
    px: { xs: 1.5, sm: 2 }, py: 1.5,
    borderTop: `1px solid ${C.border}`, bgcolor: C.paper,
    display: "flex", alignItems: "center", gap: 1,
    flexShrink: 0, // never let this collapse — keeps the input visible
  }}>
    <TextField
      placeholder={disabled ? "Connection required to start chatting…" : "Write your message..."}
      variant="outlined" fullWidth multiline maxRows={3}
      value={message} disabled={disabled}
      onChange={(e) => setMessage(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2.5, fontSize: 13,
          bgcolor: disabled ? C.borderSoft : C.primarySofter,
          "& fieldset": { borderColor: C.border },
          "&:hover fieldset": { borderColor: disabled ? C.border : C.primary },
          "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
        },
      }}
    />
    <IconButton onClick={onEmojiOpen} disabled={disabled} sx={{ color: C.textMuted, p: 0.75 }}>
      <EmojiEmotions sx={{ fontSize: 22 }} />
    </IconButton>
    {showAttach && (
      <IconButton onClick={onAttach} disabled={disabled} sx={{ color: C.textMuted, p: 0.75 }}>
        <AttachFile sx={{ fontSize: 22 }} />
      </IconButton>
    )}
    <IconButton
      onClick={onSend} disabled={!message.trim() || disabled}
      sx={{
        bgcolor: message.trim() && !disabled ? C.primary : C.borderSoft,
        color: message.trim() && !disabled ? "#fff" : C.textMuted,
        borderRadius: 2.5, p: 1, transition: "all 0.2s",
        "&:hover": { bgcolor: message.trim() && !disabled ? C.primaryDark : C.borderSoft },
      }}
    >
      <Send sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
);

// ── Emoji Popover ─────────────────────────────────────────────────────────────
const EMOJIS = [
  "😀","😃","😄","😁","😆","😅","🤣","😂","😊","😇","🙂","😉","😌","😍","🥰",
  "😘","😗","😚","😙","😋","😛","😜","🤪","😝","😑","😐","😶","😏","😒","🙄",
  "👍","👎","👏","🙌","🤝","❤️","🧡","💛","💚","💙","💜","🖤","🤍",
  "🎉","🎊","🎈","🎁","🎂","🍰","🎃",
];

const EmojiPopover = ({ anchor, onClose, onPick }) => (
  <Popover
    open={Boolean(anchor)} anchorEl={anchor} onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "center" }}
    transformOrigin={{ vertical: "bottom", horizontal: "center" }}
    PaperProps={{ sx: { borderRadius: 2.5, boxShadow: "0 4px 20px rgba(0,0,0,0.12)" } }}
  >
    <Box sx={{ p: 1.5, width: 280 }}>
      <Grid container>
        {EMOJIS.map((e, i) => (
          <Grid item xs={2} key={i}>
            <Box
              onClick={() => { onPick(e); onClose(); }}
              sx={{
                fontSize: 22, cursor: "pointer", textAlign: "center",
                p: 0.5, borderRadius: 1,
                "&:hover": { bgcolor: C.borderSoft },
              }}
            >
              {e}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Popover>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function ChatPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [message, setMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [error, setError] = useState("");
  const [userChats, setUserChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [attachmentDialog, setAttachmentDialog] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const [connectionState, setConnectionState] = useState(null);
  const [blockedChat, setBlockedChat] = useState(null);

  // Groups
  const [tabValue, setTabValue] = useState(TAB_MENTORS);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [createGroupDialog, setCreateGroupDialog] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [selectedNewMember, setSelectedNewMember] = useState(null);
  const [membersDialog, setMembersDialog] = useState(false);

  const [showChat, setShowChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [connectionStatuses, setConnectionStatuses] = useState({});

  const loggedInUser = JSON.parse(localStorage.getItem("user"));
  const loggedInUserId = loggedInUser?.id;

  // ✅ FIX: normalize role. User model uses "learner" but this component thinks
  // in terms of "mentee" everywhere (incoming learners are tagged role:"mentee"
  // at fetch time). Translate "learner" → "mentee" so logic stays consistent.
  const rawRole = (loggedInUser?.role || "").toLowerCase();
  const loggedInRole = rawRole === "learner" ? "mentee" : (rawRole || "mentee");

  const scrollToBottom = () => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const findExistingChat = (uid) =>
    userChats.find((c) => c.participants?.some((p) => p._id === uid));

  // ✅ FIX: same-role pairs (mentor↔mentor or mentee↔mentee) NEVER need a check.
  // Only cross-role pairs (mentor↔mentee) require an accepted connection.
  const pairingNeedsCheck = (otherUser) =>
    loggedInRole !== otherUser.role;

  const fetchConnectionStatus = async (otherUser) => {
    const mentorId = loggedInRole === "mentor" ? loggedInUserId : otherUser.userId;
    const menteeId = loggedInRole === "mentor" ? otherUser.userId : loggedInUserId;
    try {
      const res = await axios.get(STATUS_URL, { params: { menteeId, mentorId } });
      return res.data?.status || "none";
    } catch {
      return "none";
    }
  };

  const canDM = (otherUser) => {
    if (!pairingNeedsCheck(otherUser)) return true;
    if (findExistingChat(otherUser.userId)) return true;
    return connectionStatuses[otherUser.userId] === "accepted";
  };

  const getChatPermission = (otherUser) => {
    if (!pairingNeedsCheck(otherUser)) return "allowed";
    if (findExistingChat(otherUser.userId)) return "allowed";

    const status = connectionStatuses[otherUser.userId];
    if (status === "accepted") return "allowed";

    if (loggedInRole === "mentor") return "await-from-mentee";
    if (status === "pending") return "pending";
    return "blocked";
  };

  // ── Fetch users + chats ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [mentorRes, learnerRes, chatsRes] = await Promise.all([
          axios.get(`${API}/api/mentor/list`),
          axios.get(`${API}/api/learner/list`),
          axios.get(`${API}/api/chats/${loggedInUserId}`),
        ]);
        const mentors = (mentorRes.data?.mentors || []).map((u) => ({ ...u, role: "mentor" }));
        const learners = (learnerRes.data?.learners || []).map((u) => ({ ...u, role: "mentee" }));
        setUsers([...mentors, ...learners].filter((u) => u.userId !== loggedInUserId));
        setUserChats(chatsRes.data || []);
      } catch (err) {
        setError("Failed to load users or chats.");
      } finally {
        setLoading(false);
      }
    };
    if (loggedInUserId) fetchData();
  }, [loggedInUserId]);

  // ── Fetch groups ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/api/groups/user/${loggedInUserId}`);
        setGroups(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching groups:", err);
      }
    };
    if (loggedInUserId) fetchGroups();
  }, [loggedInUserId]);

  // ── Pre-fetch connection statuses (only for cross-role counterparts) ──────
  useEffect(() => {
    if (users.length === 0) return;

    // Only the OPPOSITE role needs a connection check — same-role chats are free
    const counterparts = users.filter((u) => u.role !== loggedInRole);
    if (counterparts.length === 0) return;

    (async () => {
      const results = await Promise.allSettled(
        counterparts.map((c) => fetchConnectionStatus(c))
      );
      const map = {};
      counterparts.forEach((c, i) => {
        map[c.userId] = results[i].status === "fulfilled" ? results[i].value : "none";
      });
      setConnectionStatuses(map);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, loggedInUserId, loggedInRole]);

  // Mirror the mentor directory's handleConnect pattern: navigate(`/myrequests/${mentor.userId}`)
  // blockedChat.userId is the target mentor's userId, set in startChat when a
  // mentee tries to open a chat that requires a connection.
  const handleNavigateToConnect = () => {
    if (blockedChat?.userId) {
      navigate(`/myrequests/${blockedChat.userId}`);
    } else {
      // Defensive fallback — shouldn't happen since the banner only renders
      // when blockedChat is set, but keeps the click from being a no-op
      navigate("/mentor");
    }
  };

  // ── Start a DM ───────────────────────────────────────────────────────────
  const startChat = async (otherUser) => {
    setSelectedUser(otherUser);
    setSelectedGroup(null);
    setActiveGroupId(null);
    setActiveChatId(null);
    setChatMessages([]);
    setError("");
    setConnectionState(null);
    setBlockedChat(null);
    if (isMobile) setShowChat(true);

    let permission = getChatPermission(otherUser);

    const notYetFetched = connectionStatuses[otherUser.userId] === undefined;
    if (permission !== "allowed" && pairingNeedsCheck(otherUser) &&
        !findExistingChat(otherUser.userId) && notYetFetched) {
      const fresh = await fetchConnectionStatus(otherUser);
      setConnectionStatuses((prev) => ({ ...prev, [otherUser.userId]: fresh }));
      if (fresh === "accepted") {
        permission = "allowed";
      } else if (loggedInRole === "mentor") {
        permission = "await-from-mentee";
      } else {
        permission = fresh === "pending" ? "pending" : "blocked";
      }
    }

    if (permission !== "allowed") {
      setConnectionState(permission);
      setBlockedChat({ userId: otherUser.userId, userName: otherUser.fullName });
      return;
    }

    try {
      let chat = findExistingChat(otherUser.userId);
      if (!chat) {
        try {
          const res = await axios.post(`${API}/api/chats`, {
            userId1: loggedInUserId, userId2: otherUser.userId,
          });
          chat = res.data;
          setUserChats((prev) => [...prev, chat]);
        } catch (chatErr) {
          // ✅ FIX: only treat a 403 as "connection required" for cross-role
          // pairs. Same-role (mentor↔mentor / mentee↔mentee) chats are
          // always free on the frontend, so a 403 here can't mean a missing
          // connection — surface it as a normal error instead of incorrectly
          // showing the blocked/pending/await-from-mentee banners.
          if (chatErr.response?.status === 403 && pairingNeedsCheck(otherUser)) {
            const fresh = await fetchConnectionStatus(otherUser);
            setConnectionStatuses((prev) => ({ ...prev, [otherUser.userId]: fresh }));
            const newState = loggedInRole === "mentor"
              ? "await-from-mentee"
              : fresh === "pending" ? "pending" : "blocked";
            setConnectionState(newState);
            setBlockedChat({ userId: otherUser.userId, userName: otherUser.fullName });
            return;
          }
          if (chatErr.response?.status === 403) {
            setError("Unable to start chat — server rejected the request.");
            return;
          }
          throw chatErr;
        }
      }

      setActiveChatId(chat._id);
      const msgRes = await axios.get(`${API}/api/messages/${chat._id}`);
      setChatMessages(msgRes.data.map((m) => ({
        id: m._id, text: m.text,
        sender: m.sender?._id || m.sender,
        time: new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        type: (m.sender?._id || m.sender) === loggedInUserId ? "sent" : "received",
      })));
      scrollToBottom();
    } catch (err) {
      setError("Failed to load chat history.");
    }
  };

  // ── Open a group ───────────────────────────────────────────────────────────
  const handleGroupSelect = async (group) => {
    try {
      setSelectedGroup(group);
      setActiveGroupId(group._id);
      setActiveChatId(null);
      setSelectedUser(null);
      setGroupMessages([]);
      setConnectionState(null);
      setBlockedChat(null);

      const res = await axios.get(`${API}/api/groups/messages/${group._id}`);
      setSelectedGroup({ ...group, name: res.data.name || group.name, users: res.data.users || group.users });
      setGroupMessages((res.data.messages || []).map((msg) => ({
        id: msg._id, text: msg.text,
        sender: msg.sender?._id || msg.sender,
        senderName: msg.sender?.fullName || "User",
        senderEmail: msg.sender?.email,
        time: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        type: (msg.sender?._id || msg.sender) === loggedInUserId ? "sent" : "received",
      })));
      if (isMobile) setShowChat(true);
      scrollToBottom();
    } catch (err) {
      setError("Failed to load group chat.");
    }
  };

  // ── Send a message ───────────────────────────────────────────────────────
  const handleSend = async () => {
    if (attachedFiles.length > 0) { await handleSendWithAttachments(); return; }
    if (!message.trim()) return;

    try {
      if (activeGroupId) {
        const res = await axios.post(`${API}/api/groups/send-message`, {
          chatId: activeGroupId, sender: loggedInUserId, text: message.trim(),
        });
        setGroupMessages((prev) => [...prev, {
          id: res.data._id, text: res.data.text, sender: loggedInUserId,
          senderName: loggedInUser?.fullName || "You",
          time: new Date(res.data.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          type: "sent",
        }]);
      } else if (activeChatId) {
        const res = await axios.post(`${API}/api/messages/`, {
          chatId: activeChatId, sender: loggedInUserId, text: message.trim(),
        });
        setChatMessages((prev) => [...prev, {
          id: res.data._id, text: res.data.text, sender: loggedInUserId,
          time: new Date(res.data.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          type: "sent",
        }]);
      }
      setMessage("");
      scrollToBottom();
    } catch (err) {
      if (err.response?.status === 403 && selectedUser && pairingNeedsCheck(selectedUser)) {
        setConnectionState(loggedInRole === "mentor" ? "await-from-mentee" : "blocked");
        setBlockedChat({ userId: selectedUser?.userId, userName: selectedUser?.fullName });
        setActiveChatId(null);
      } else if (err.response?.status === 403) {
        // ✅ FIX: same-role 403 — never show the connection-required UI
        setError("Unable to send message — server rejected the request.");
      } else {
        setError("Failed to send message.");
      }
    }
  };

  const handleSendWithAttachments = async () => {
    if (!activeChatId) return;
    try {
      const formData = new FormData();
      formData.append("chatId", activeChatId);
      formData.append("sender", loggedInUserId);
      formData.append("text", message.trim());
      attachedFiles.forEach((f) => formData.append("attachments", f));
      const res = await axios.post(`${API}/api/messages/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setChatMessages((prev) => [...prev, {
        id: res.data._id, text: res.data.text, sender: loggedInUserId,
        time: new Date(res.data.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        type: "sent", attachments: res.data.attachments || [],
      }]);
      setMessage(""); setAttachedFiles([]); setAttachmentDialog(false);
      scrollToBottom();
    } catch (err) {
      if (err.response?.status === 403 && selectedUser && pairingNeedsCheck(selectedUser)) {
        setConnectionState(loggedInRole === "mentor" ? "await-from-mentee" : "blocked");
        setBlockedChat({ userId: selectedUser?.userId, userName: selectedUser?.fullName });
        setActiveChatId(null);
        setAttachmentDialog(false);
      } else if (err.response?.status === 403) {
        // ✅ FIX: same-role 403 — never show the connection-required UI
        setError("Unable to send message — server rejected the request.");
      } else {
        setError("Failed to send message.");
      }
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedGroupMembers.length === 0) return;
    try {
      const res = await axios.post(`${API}/api/groups/create`, {
        name: groupName, admin: loggedInUserId,
        users: [loggedInUserId, ...selectedGroupMembers.map((u) => u.userId)],
      });
      setGroups((prev) => [...prev, res.data]);
      setGroupName(""); setSelectedGroupMembers([]); setCreateGroupDialog(false);
    } catch (err) {
      setError("Failed to create group.");
    }
  };

  const handleAddMember = async () => {
    if (!selectedNewMember || !activeGroupId) return;
    try {
      await axios.post(`${API}/api/groups/add-user`, {
        groupId: activeGroupId, userId: selectedNewMember.userId,
      });
      const added = {
        _id: selectedNewMember.userId,
        fullName: selectedNewMember.fullName,
        email: selectedNewMember.email,
        role: selectedNewMember.role,
      };
      setSelectedGroup((prev) =>
        prev ? { ...prev, users: [...(prev.users || []), added] } : prev
      );
      setSelectedNewMember(null); setAddMemberDialog(false);
    } catch (err) {
      setError("Failed to add member.");
    }
  };

  // ── Sidebar ordering: recent chats → recent connections → others ───────────
  const rankedUsers = React.useMemo(() => {
    const chatRecency = new Map();
    userChats.forEach((c) => {
      const ts = c.updatedAt || c.lastMessageAt || c.createdAt || 0;
      c.participants?.forEach((p) => {
        if (p._id !== loggedInUserId) chatRecency.set(p._id, ts);
      });
    });

    const tierOf = (u) => {
      if (chatRecency.has(u.userId)) return 0;
      // Same-role users always have implicit access — no connection state
      if (u.role === loggedInRole) return 1;
      const s = connectionStatuses[u.userId];
      if (s === "accepted" || s === "pending") return 1;
      return 2;
    };

    return [...users].sort((a, b) => {
      const ta = tierOf(a), tb = tierOf(b);
      if (ta !== tb) return ta - tb;
      if (ta === 0) {
        return new Date(chatRecency.get(b.userId)) - new Date(chatRecency.get(a.userId));
      }
      return (a.fullName || "").localeCompare(b.fullName || "");
    });
  }, [users, userChats, connectionStatuses, loggedInUserId, loggedInRole]);

  // ── Tab-specific filtered lists ────────────────────────────────────────────
  const usersForTab = React.useMemo(() => {
    if (tabValue === TAB_MENTORS) return rankedUsers.filter((u) => u.role === "mentor");
    if (tabValue === TAB_MENTEES) return rankedUsers.filter((u) => u.role === "mentee");
    return [];
  }, [rankedUsers, tabValue]);

  const filteredUsers = usersForTab.filter((u) =>
    u.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGroups = groups.filter((g) =>
    g.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getUserLockState = (user) => {
    if (!pairingNeedsCheck(user)) return null; // same role = no lock
    if (findExistingChat(user.userId)) return null;
    const status = connectionStatuses[user.userId];
    if (status === "accepted") return null;
    if (status === "pending") return "pending";
    return "locked";
  };

  const addableUsers = (group) => {
    const memberIds = group?.users?.map((x) => (typeof x === "object" ? x._id : x)) || [];
    return users.filter((u) => !memberIds.includes(u.userId) && canDM(u));
  };

  const normalizeMember = (m) =>
    typeof m === "object"
      ? { _id: m._id, fullName: m.fullName, email: m.email, role: m.role }
      : { _id: m, fullName: "Member", email: "", role: undefined };

  const emptyCopy = (() => {
    if (tabValue === TAB_MENTORS) return "No mentors found";
    if (tabValue === TAB_MENTEES) return "No mentees found";
    return "No groups yet. Create one!";
  })();

  // ── Sidebar ──────────────────────────────────────────────────────────────
  const SidebarList = () => (
    // ✅ FIX: explicit min-height: 0 + flex column ensures inner scroll works
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Tabs
        value={tabValue}
        onChange={(_, v) => { setTabValue(v); setSearchQuery(""); }}
        variant="fullWidth"
        sx={{
          borderBottom: `1px solid ${C.border}`, minHeight: 44,
          flexShrink: 0,
          "& .MuiTab-root": {
            textTransform: "none", fontWeight: 700, fontSize: 12.5,
            minHeight: 44, px: 0.5, color: C.textSecondary,
          },
          "& .MuiTabs-indicator": { bgcolor: C.primary, height: 3 },
          "& .Mui-selected": { color: `${C.primary} !important` },
        }}
      >
        <Tab icon={<MentorIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Mentors" />
        <Tab icon={<MenteeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Mentees" />
        <Tab icon={<GroupIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Groups" />
      </Tabs>

      <Box sx={{ px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Paper sx={{
          display: "flex", alignItems: "center", borderRadius: 2.5,
          px: 1.5, border: `1px solid ${C.border}`, bgcolor: C.primarySofter, boxShadow: "none",
        }}>
          <Search sx={{ fontSize: 18, color: C.textMuted, mr: 0.75 }} />
          <InputBase
            placeholder={
              tabValue === TAB_MENTORS ? "Search mentors..." :
              tabValue === TAB_MENTEES ? "Search mentees..." :
                                          "Search groups..."
            }
            fullWidth value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ fontSize: 13, py: 0.75 }}
          />
        </Paper>
      </Box>

      {/* People tabs */}
      {(tabValue === TAB_MENTORS || tabValue === TAB_MENTEES) && (
        <Box sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0, // critical for the scroll to engage
          // Custom scrollbar
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: C.border, borderRadius: 3 },
          "&::-webkit-scrollbar-thumb:hover": { bgcolor: C.textMuted },
        }}>
          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress size={36} sx={{ color: C.primary }} />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Typography color={C.textSecondary} align="center" mt={4} fontSize={13}>{emptyCopy}</Typography>
          ) : filteredUsers.map((user, idx) => {
            const lockState = getUserLockState(user);
            const isSelected = selectedUser?.userId === user.userId;
            return (
              <React.Fragment key={user.userId || idx}>
                <ListItem
                  button
                  onClick={() => startChat(user)}
                  selected={isSelected}
                  sx={{
                    px: 2, py: 1.25, borderRadius: 0,
                    "&.Mui-selected": { bgcolor: C.primarySoft },
                    "&:hover": { bgcolor: C.primarySofter },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 46 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        <Box sx={{
                          width: 9, height: 9, borderRadius: "50%",
                          bgcolor: C.secondaryLight,
                          border: `1.5px solid ${C.paper}`,
                        }} />
                      }
                    >
                      <Avatar sx={{
                        width: 38, height: 38,
                        background: user.role === "mentor"
                          ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`
                          : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                        color: "#fff",
                        fontSize: 15, fontWeight: 700,
                      }}>
                        {user.fullName?.charAt(0) || "U"}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.textPrimary }}>
                          {user.fullName}
                        </Typography>
                        <RoleTag role={user.role} />
                        {lockState === "locked" && (
                          <LockIcon sx={{ fontSize: 12, color: C.warning }} />
                        )}
                        {lockState === "pending" && (
                          <PendingIcon sx={{ fontSize: 12, color: C.primary }} />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Typography sx={{ fontSize: 11, color: C.textMuted }} noWrap>{user.email}</Typography>
                    }
                  />
                </ListItem>
                <Divider sx={{ ml: 8, borderColor: C.borderSoft }} />
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {/* Groups tab */}
      {tabValue === TAB_GROUPS && (
        <Box sx={{
          flex: 1, overflowY: "auto", minHeight: 0,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: C.border, borderRadius: 3 },
          "&::-webkit-scrollbar-thumb:hover": { bgcolor: C.textMuted },
        }}>
          <Box sx={{ px: 1.5, pb: 1, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={() => setCreateGroupDialog(true)}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: 12,
                color: C.primary, bgcolor: C.primarySoft, borderRadius: 2,
                px: 1.5, "&:hover": { bgcolor: alpha(C.primary, 0.18) },
              }}
            >
              New Group
            </Button>
          </Box>
          {filteredGroups.length === 0 ? (
            <Typography color={C.textSecondary} align="center" mt={3} fontSize={13}>{emptyCopy}</Typography>
          ) : filteredGroups.map((group, idx) => (
            <React.Fragment key={group._id || idx}>
              <ListItem
                button
                onClick={() => handleGroupSelect(group)}
                selected={selectedGroup?._id === group._id}
                sx={{
                  px: 2, py: 1.25,
                  "&.Mui-selected": { bgcolor: C.primarySoft },
                  "&:hover": { bgcolor: C.primarySofter },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 46 }}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: C.group, fontSize: 13, fontWeight: 700 }}>
                    {group.name?.charAt(0)?.toUpperCase() || "G"}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography sx={{ fontWeight: 700, fontSize: 13, color: C.textPrimary }}>{group.name}</Typography>}
                  secondary={<Typography sx={{ fontSize: 11, color: C.textMuted }}>{group.users?.length || 0} members</Typography>}
                />
              </ListItem>
              <Divider sx={{ ml: 8, borderColor: C.borderSoft }} />
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );

  // ── Chat header ────────────────────────────────────────────────────────────
  const ChatHeader = () => {
    const isGroup = !!selectedGroup;
    const name = isGroup ? (selectedGroup.groupName || selectedGroup.name) : selectedUser?.fullName;
    const memberCount = selectedGroup?.users?.length || 0;
    const sub = isGroup ? `${memberCount} members` : "Online";
    const otherUserRole = selectedUser?.role;
    const restricted = !isGroup && !!connectionState;

    const headerIcon =
      connectionState === "blocked" ? <LockIcon sx={{ fontSize: 11, color: C.warning }} /> :
      connectionState === "pending" ? <PendingIcon sx={{ fontSize: 11, color: C.primary }} /> :
      connectionState === "await-from-mentee" ? <InfoIcon sx={{ fontSize: 11, color: C.textSecondary }} /> :
      null;

    const headerText =
      connectionState === "blocked" ? "Connection required" :
      connectionState === "pending" ? "Request pending" :
      connectionState === "await-from-mentee" ? "Awaiting their request" :
      null;

    const headerColor =
      connectionState === "blocked" ? C.warning :
      connectionState === "pending" ? C.primary :
      connectionState === "await-from-mentee" ? C.textSecondary :
      C.textSecondary;

    return (
      <Box sx={{
        px: { xs: 1.5, sm: 2 }, py: 1.5,
        borderBottom: `1px solid ${C.border}`, bgcolor: C.paper,
        display: "flex", alignItems: "center", gap: 1.5,
        flexShrink: 0, // never let this collapse
      }}>
        {isMobile && (
          <IconButton size="small" onClick={() => setShowChat(false)} sx={{ color: C.textPrimary, mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Avatar sx={{
          width: 38, height: 38,
          background: isGroup
            ? C.group
            : otherUserRole === "mentor"
              ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`
              : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
          color: "#fff",
          fontSize: 15, fontWeight: 700,
        }}>
          {name?.charAt(0)?.toUpperCase() || (isGroup ? "G" : "U")}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" spacing={0.75} mb={0.1}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary }} noWrap>{name}</Typography>
            {!isGroup && otherUserRole && <RoleTag role={otherUserRole} />}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {headerIcon}
            {!restricted && !isGroup && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: C.secondaryLight }} />}
            <Typography
              sx={{ fontSize: 11, color: headerColor, cursor: isGroup ? "pointer" : "default", fontWeight: 600 }}
              onClick={isGroup ? () => setMembersDialog(true) : undefined}
            >
              {headerText}{isGroup ? " · View" : ""}
            </Typography>
          </Stack>
        </Box>
        {isGroup && (
          <>
            <AvatarGroup
              max={4}
              onClick={() => setMembersDialog(true)}
              sx={{ cursor: "pointer", "& .MuiAvatar-root": { width: 26, height: 26, fontSize: 11 } }}
            >
              {(selectedGroup.users || []).map((m, i) => {
                const mm = normalizeMember(m);
                return (
                  <Avatar key={mm._id || i} sx={{
                    background: mm.role === "mentor"
                      ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`
                      : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                  }}>
                    {mm.fullName?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>
                );
              })}
            </AvatarGroup>
            <IconButton size="small" onClick={() => setAddMemberDialog(true)} sx={{ color: C.primary }}>
              <PersonAddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </>
        )}
      </Box>
    );
  };

  // ── Messages area ──────────────────────────────────────────────────────────
  const MessagesArea = () => {
    const msgs = selectedGroup ? groupMessages : chatMessages;
    return (
      <Box sx={{
        flex: 1,
        overflowY: "auto",
        minHeight: 0,
        px: { xs: 1.5, sm: 2.5 },
        py: 1.5, // ✅ slightly less padding so input bar sits visually closer
        bgcolor: C.bg,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { bgcolor: C.border, borderRadius: 3 },
        "&::-webkit-scrollbar-thumb:hover": { bgcolor: C.textMuted },
      }}>
        {msgs.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography color={C.textSecondary} fontSize={13}>
              {selectedGroup ? "No group messages yet." : "No messages yet. Start the conversation!"}
            </Typography>
          </Box>
        ) : msgs.map((msg) => {
          const isSent = msg.type === "sent";
          const senderInitial = (msg.senderName || selectedUser?.fullName || "U").charAt(0).toUpperCase();
          return (
            <Bubble
              key={msg.id} msg={msg} isSent={isSent}
              senderName={selectedGroup && !isSent ? msg.senderName : null}
              senderInitial={senderInitial}
            />
          );
        })}
        <div ref={chatEndRef} />
      </Box>
    );
  };

  const activeConversation = selectedUser || selectedGroup;
  const isChatBlocked = connectionState === "blocked" && !selectedGroup;
  const isChatPending = connectionState === "pending" && !selectedGroup;
  const isChatAwaitMentee = connectionState === "await-from-mentee" && !selectedGroup;
  const isChatRestricted = isChatBlocked || isChatPending || isChatAwaitMentee;

  const restrictedEmptyIcon = isChatBlocked
    ? <LockIcon sx={{ fontSize: 44, color: alpha(C.warning, 0.4) }} />
    : isChatPending
      ? <PendingIcon sx={{ fontSize: 44, color: alpha(C.primary, 0.4) }} />
      : <InfoIcon sx={{ fontSize: 44, color: C.textMuted }} />;

  const restrictedEmptyTitle = isChatBlocked
    ? "No messages yet"
    : isChatPending
      ? "Awaiting approval"
      : "Not connected yet";

  const restrictedEmptyBody = isChatBlocked
    ? "Messages will appear here once your connection is accepted"
    : isChatPending
      ? "The request has been sent. You'll be able to chat once it's accepted"
      : "Chat opens once this mentee sends you a connection request and you accept it";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        mx: { xs: 0, sm: 2, md: 5, lg: 10 },
        my: { xs: 0, md: 2 },
        minHeight: 0, // ensures the chat container can constrain its children
      }}>
        {/* ── Hero header (compact) ────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: { xs: 0, sm: 3 }, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            mb: { xs: 0, sm: 2 },
            color: "#fff",
            boxShadow: `0 4px 16px ${C.primary}25`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-50%", right: "-10%",
            width: 200, height: 200, borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <ChatIcon sx={{ fontSize: 22, color: "rgba(255,255,255,0.85)" }} />
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, color: "#fff", letterSpacing: -0.3 }}>
                Chats
              </Typography>
            </Stack>
            <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: 12, sm: 13 }, mt: 0.25 }}>
              Direct messages, peer conversations, and group spaces
            </Typography>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            bgcolor: C.paper,
            borderRadius: { xs: 0, sm: 3 },
            border: { xs: "none", sm: `1px solid ${C.border}` },
            overflow: "hidden",
            // ✅ FIX: more generous height — pushes input bar higher into viewport
            height: { xs: "calc(100vh - 220px)", md: "calc(100vh - 240px)" },
            minHeight: { xs: 500, md: 560 },
            maxHeight: { md: "calc(100vh - 180px)" },
            boxShadow: { xs: "none", sm: "0 2px 12px rgba(13,17,23,0.05)" },
            minWidth: 0,
          }}
        >
          {/* Sidebar */}
          {(!isMobile || !showChat) && (
            <Box sx={{
              width: { xs: "100%", md: 310 }, flexShrink: 0,
              borderRight: { md: `1px solid ${C.border}` },
              display: "flex", flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
            }}>
              <SidebarList />
            </Box>
          )}

          {/* Chat pane */}
          {(!isMobile || showChat) && (
            <Box sx={{
              flex: 1,
              display: "flex", flexDirection: "column",
              minWidth: 0, minHeight: 0,
              overflow: "hidden",
            }}>
              {activeConversation ? (
                <>
                  <ChatHeader />

                  {isChatRestricted ? (
                    <Box sx={{
                      flex: 1, overflowY: "auto", minHeight: 0,
                      bgcolor: C.bg,
                      "&::-webkit-scrollbar": { width: 6 },
                      "&::-webkit-scrollbar-thumb": { bgcolor: C.border, borderRadius: 3 },
                    }}>
                      {isChatBlocked && (
                        <MenteeBlockedBanner
                          mentorName={blockedChat?.userName}
                          onNavigate={handleNavigateToConnect}
                        />
                      )}
                      {isChatPending && (
                        <PendingConnectionBanner mentorName={blockedChat?.userName} />
                      )}
                      {isChatAwaitMentee && (
                        <MentorAwaitMenteeBanner menteeName={blockedChat?.userName} />
                      )}
                      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={4} gap={1}>
                        {restrictedEmptyIcon}
                        <Typography color={C.textPrimary} fontSize={13} fontWeight={700}>
                          {restrictedEmptyTitle}
                        </Typography>
                        <Typography color={C.textSecondary} fontSize={12} textAlign="center" px={3}>
                          {restrictedEmptyBody}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {attachedFiles.length > 0 && (
                        <Box sx={{
                          px: 2, py: 1,
                          bgcolor: C.primarySofter, borderBottom: `1px solid ${C.border}`,
                          flexShrink: 0,
                        }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                            {attachedFiles.map((file, idx) => (
                              <Chip
                                key={idx} label={file.name} size="small"
                                onDelete={() => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))}
                                icon={file.type.startsWith("image/") ? <ImageIcon /> : file.type.startsWith("video/") ? <VideoIcon /> : <DocumentIcon />}
                                sx={{ maxWidth: 180, fontSize: 11 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      <MessagesArea />
                    </>
                  )}

                  <InputBar
                    message={message} setMessage={setMessage}
                    onSend={handleSend}
                    onEmojiOpen={(e) => setEmojiAnchorEl(e.currentTarget)}
                    onAttach={() => setAttachmentDialog(true)}
                    showAttach={!!activeChatId}
                    disabled={isChatRestricted}
                  />

                  <EmojiPopover anchor={emojiAnchorEl} onClose={() => setEmojiAnchorEl(null)} onPick={(e) => setMessage((p) => p + e)} />
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={1}>
                  <ChatIcon sx={{ fontSize: 52, color: C.textMuted, opacity: 0.5 }} />
                  <Typography color={C.textPrimary} fontWeight={700} fontSize={14}>Select a conversation</Typography>
                  <Typography color={C.textSecondary} fontSize={12}>Choose a user or group from the sidebar</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Group Members Dialog */}
      <Dialog open={membersDialog} onClose={() => setMembersDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
          {(selectedGroup?.groupName || selectedGroup?.name || "Group")} · {selectedGroup?.users?.length || 0} members
        </DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 340, overflowY: "auto" }}>
            {(selectedGroup?.users || []).map((m, idx) => {
              const mm = normalizeMember(m);
              const isMe = mm._id === loggedInUserId;
              return (
                <ListItem key={mm._id || idx} sx={{ borderRadius: 2, mb: 0.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{
                      width: 34, height: 34,
                      background: mm.role === "mentor"
                        ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`
                        : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                      color: "#fff",
                    }}>
                      {mm.fullName?.charAt(0)?.toUpperCase() || "U"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography fontSize={13} fontWeight={600}>{mm.fullName}{isMe ? " (You)" : ""}</Typography>
                        {mm.role && <RoleTag role={mm.role} />}
                      </Stack>
                    }
                    secondary={mm.email ? <Typography fontSize={11} color={C.textMuted}>{mm.email}</Typography> : null}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setMembersDialog(false); setAddMemberDialog(true); }}
            startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", borderRadius: 2.5, color: C.primary, fontWeight: 700 }}>
            Add member
          </Button>
          <Button onClick={() => setMembersDialog(false)} variant="contained"
            sx={{
              textTransform: "none", borderRadius: 2.5, fontWeight: 700,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, boxShadow: "none",
              "&:hover": { boxShadow: `0 4px 12px ${C.primary}30` },
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Attachment Dialog */}
      <Dialog open={attachmentDialog} onClose={() => setAttachmentDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>Attach Files</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <input ref={fileInputRef} type="file" multiple onChange={(e) => setAttachedFiles((prev) => [...prev, ...Array.from(e.target.files)])} style={{ display: "none" }} />
          <Stack spacing={1.5} mb={2}>
            {[
              { label: "Images", icon: <ImageIcon />, accept: "image/*" },
              { label: "Videos", icon: <VideoIcon />, accept: "video/*" },
              { label: "Documents", icon: <DocumentIcon />, accept: ".pdf,.doc,.docx,.xls,.xlsx,.txt" },
            ].map((item) => (
              <Button key={item.label} variant="outlined" startIcon={item.icon} fullWidth
                sx={{
                  borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
                  borderColor: C.border, color: C.textPrimary,
                  "&:hover": { borderColor: C.primary, bgcolor: C.primarySofter },
                }}
                onClick={() => { fileInputRef.current.accept = item.accept; fileInputRef.current.click(); }}>
                Add {item.label}
              </Button>
            ))}
          </Stack>
          {attachedFiles.length > 0 && (
            <Stack spacing={1}>
              {attachedFiles.map((file, idx) => (
                <Box key={idx} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.25, bgcolor: C.primarySofter, borderRadius: 2.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {file.type.startsWith("image/") ? <ImageIcon sx={{ color: C.primary }} /> : <DocumentIcon sx={{ color: C.primary }} />}
                    <Box>
                      <Typography fontSize={13} fontWeight={700}>{file.name}</Typography>
                      <Typography fontSize={11} color={C.textMuted}>{(file.size / 1024).toFixed(1)} KB</Typography>
                    </Box>
                  </Stack>
                  <IconButton size="small" onClick={() => setAttachedFiles((p) => p.filter((_, i) => i !== idx))}><CloseIcon fontSize="small" /></IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAttachmentDialog(false)} sx={{ textTransform: "none", borderRadius: 2.5, color: C.textSecondary, fontWeight: 700 }}>Cancel</Button>
          <Button onClick={() => setAttachmentDialog(false)} variant="contained" disabled={attachedFiles.length === 0}
            sx={{
              textTransform: "none", borderRadius: 2.5, fontWeight: 700,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, boxShadow: "none",
              "&:hover": { boxShadow: `0 4px 12px ${C.primary}30` },
            }}>
            Done ({attachedFiles.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={createGroupDialog} onClose={() => setCreateGroupDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>Create New Group</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField label="Group Name" fullWidth value={groupName} onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g., Project Team"
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
            }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5, color: C.textPrimary }}>Select Members</Typography>
          <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 1 }}>
            You can add people you already chat with or are connected to.
          </Typography>
          <List sx={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 2.5 }}>
            {users.filter(canDM).length === 0 ? (
              <Typography color={C.textSecondary} align="center" py={3} fontSize={13}>
                No eligible people yet. Connect with someone first.
              </Typography>
            ) : users.filter(canDM).map((user, idx) => (
              <ListItem key={user.userId || idx} button
                onClick={() => setSelectedGroupMembers((prev) =>
                  prev.find((u) => u.userId === user.userId) ? prev.filter((u) => u.userId !== user.userId) : [...prev, user]
                )}
                sx={{ py: 0.75 }}
              >
                <Checkbox checked={selectedGroupMembers.some((u) => u.userId === user.userId)} size="small"
                  sx={{ color: C.primary, "&.Mui-checked": { color: C.primary } }} />
                <ListItemAvatar sx={{ minWidth: 42 }}>
                  <Avatar sx={{
                    width: 32, height: 32,
                    background: user.role === "mentor"
                      ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`
                      : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                    color: "#fff", fontSize: 13,
                  }}>
                    {user.fullName?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography fontSize={13} fontWeight={600}>{user.fullName}</Typography>
                      <RoleTag role={user.role} />
                    </Stack>
                  }
                  secondary={<Typography fontSize={11} color={C.textMuted}>{user.email}</Typography>}
                />
              </ListItem>
            ))}
          </List>
          {selectedGroupMembers.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5}>
              {selectedGroupMembers.map((m, i) => (
                <Chip key={i} label={m.fullName} size="small"
                  onDelete={() => setSelectedGroupMembers((prev) => prev.filter((u) => u.userId !== m.userId))}
                  avatar={<Avatar>{m.fullName?.charAt(0)}</Avatar>}
                  sx={{ fontSize: 12, height: 26, bgcolor: C.primarySoft, color: C.primary, fontWeight: 600 }} />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setCreateGroupDialog(false); setGroupName(""); setSelectedGroupMembers([]); }}
            sx={{ textTransform: "none", borderRadius: 2.5, color: C.textSecondary, fontWeight: 700 }}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained"
            disabled={!groupName.trim() || selectedGroupMembers.length === 0}
            sx={{
              textTransform: "none", borderRadius: 2.5, fontWeight: 700,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, boxShadow: "none",
              "&:hover": { boxShadow: `0 4px 12px ${C.primary}30` },
            }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialog} onClose={() => setAddMemberDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>Add Member</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 1 }}>
            You can add people you already chat with or are connected to.
          </Typography>
          <List sx={{ maxHeight: 300, overflowY: "auto" }}>
            {addableUsers(selectedGroup).length === 0 ? (
              <Typography color={C.textSecondary} align="center" py={3} fontSize={13}>
                No one available to add right now.
              </Typography>
            ) : addableUsers(selectedGroup).map((user, idx) => (
              <ListItem key={user.userId || idx} button onClick={() => setSelectedNewMember(user)}
                selected={selectedNewMember?.userId === user.userId}
                sx={{ borderRadius: 2, mb: 0.5, "&.Mui-selected": { bgcolor: C.primarySoft } }}>
                <ListItemAvatar>
                  <Avatar sx={{
                    width: 34, height: 34,
                    background: user.role === "mentor"
                      ? `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`
                      : `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                    color: "#fff",
                  }}>
                    {user.fullName?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography fontSize={13} fontWeight={600}>{user.fullName}</Typography>
                      <RoleTag role={user.role} />
                    </Stack>
                  }
                  secondary={<Typography fontSize={11} color={C.textMuted}>{user.email}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddMemberDialog(false); setSelectedNewMember(null); }}
            sx={{ textTransform: "none", borderRadius: 2.5, color: C.textSecondary, fontWeight: 700 }}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" disabled={!selectedNewMember}
            sx={{
              textTransform: "none", borderRadius: 2.5, fontWeight: 700,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, boxShadow: "none",
              "&:hover": { boxShadow: `0 4px 12px ${C.primary}30` },
            }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {!isMobile && <Footer />}
    </Box>
  );
}